import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@apollo/client';
import { Types } from '@scorenco/core';
import { endOfWeek, startOfWeek } from 'date-fns';

import {
  GetEventsBetweenDateDocument,
  GetPlannerTasksDocument,
  GetUserPlannerSettingsDocument,
  GetClubPlannerSettingsDocument,
} from './MatchsPlanner.query.generated';

export const MatchsPlannerContext = createContext<
  MatchsPlannerContextProps | undefined
>(undefined);

export const useMatchsPlannerContext = (): MatchsPlannerContextProps => {
  const context = useContext(MatchsPlannerContext);

  if (!context) {
    throw new Error(
      'useMatchsPlannerContext must be inside an MatchsPlannerProvider'
    );
  }

  return context;
};

export type MatchsPlannerContextProps = {
  clubId: number;
  events?: Types.Competitions_Event_Detail[];
  startDate: Date;
  endDate: Date;
  changeDate: (values: { startDate?: Date; endDate?: Date }) => void;
  isLoading: boolean;
  tasks?: Types.Tasks_Task[];
  homeFilter: boolean;
  setHomeFilter: (value: boolean) => void;
  awayFilter: boolean;
  setAwayFilter: (value: boolean) => void;
  equipmentFilter: number | null;
  setEquipmentFilter: (value: number) => void;
  teamFilter: number[] | null;
  setTeamFilter: (value: number[]) => void;
  displaySettings: any;
};

type MatchsPlannerProviderProps = PropsWithChildren<{
  clubId: number;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  usePlannerInfo?: boolean;
  disablePlannerSettings?: boolean;
  forceShowLicence?: boolean;
}>;

export const MatchsPlannerProvider = ({
  clubId,
  defaultStartDate,
  defaultEndDate,
  usePlannerInfo,
  children,
  disablePlannerSettings = false,
  forceShowLicence = false,
}: MatchsPlannerProviderProps) => {
  const [dates, setDates] = useState({
    startDate: startOfWeek(defaultStartDate || defaultEndDate || new Date(), {
      weekStartsOn: 1,
    }),
    endDate: endOfWeek(defaultStartDate || defaultEndDate || new Date(), {
      weekStartsOn: 1,
    }),
    // startDate:
    //   defaultStartDate ||
    //   startOfWeek(new Date(), {
    //     weekStartsOn: 1,
    //   }),
    // endDate:
    //   defaultStartDate ||
    //   endOfWeek(new Date(), {
    //     weekStartsOn: 1,
    //   }),
  });

  const query = useQuery(GetEventsBetweenDateDocument, {
    variables: {
      clubId,
      startDate: dates.startDate,
      endDate: dates.endDate,
      usePlannerInfo: usePlannerInfo || false,
      getFedeUrl: true,
    },
    skip: !clubId,
    context: {
      role: 'anonymous',
    },
  });

  const events = query.data?.competitions_event_detail_by_club_id_between_dates;

  const tasksQuery = useQuery(GetPlannerTasksDocument, {
    variables: {
      clubId,
    },
    skip: !clubId,
    context: {
      role: 'club',
    },
  });

  // const { checkPermission } = usePermissionsContext();

  // const plannerMatchsInformationsPermission = checkPermission(
  //   'planner_event_information'
  // );
  // const plannerRefereesPermission = checkPermission('planner_referees');
  // const plannerScoreManagersPermission = checkPermission(
  //   'planner_score_managers'
  // );
  // const plannerStadiumManagersPermission = checkPermission(
  //   'planner_stadium_managers'
  // );
  // const plannerBarManagersPermission = checkPermission('planner_bar_managers');

  const getUserSettings = useQuery(GetUserPlannerSettingsDocument, {
    context: {
      role: 'user',
    },
    skip: disablePlannerSettings,
  });
  const getClubSettings = useQuery(GetClubPlannerSettingsDocument, {
    variables: {
      clubId: clubId,
    },
    context: {
      role: 'club',
    },
    skip: disablePlannerSettings,
  });
  const tasks = tasksQuery.data?.tasks_task;

  const displaySettings = useMemo(() => {
    return {
      // date: plannerMatchsInformationsPermission?.read,
      // time: plannerMatchsInformationsPermission?.read,
      // meeting: plannerMatchsInformationsPermission?.read,
      // referee: plannerRefereesPermission?.read,
      // scoringTable: plannerScoreManagersPermission?.read,
      // stadiumManager: plannerStadiumManagersPermission?.read,
      // bar: plannerBarManagersPermission?.read,
      plannerEmailCopy:
        getClubSettings.data?.clubs_planner_settings?.[0]?.email_copy || '',
      planner_email_footer_text:
        getClubSettings.data?.clubs_planner_settings?.[0]?.email_footer_text ||
        '',
      planner_convocation_link:
        getClubSettings.data?.clubs_planner_settings?.[0]?.convocation_link ||
        '',
      ...getUserSettings.data?.users_user?.[0]?.planner_settings,
      tasks: tasks?.map((t) => ({
        id: t.id,
        name: t.name,
        show:
          getUserSettings.data?.users_user?.[0]?.planner_settings?.tasks?.find(
            (t2: { id: number | null }) => t2.id === t.id
          )?.show ?? true,
      })),
      sendEmail: getClubSettings.data?.clubs_planner_settings?.[0]?.send_email,
      sendSms: getClubSettings.data?.clubs_planner_settings?.[0]?.send_sms,
      showLicence:
        getClubSettings.data?.clubs_planner_settings?.[0]?.show_licence ||
        forceShowLicence ||
        false,
    };
  }, [getUserSettings, getClubSettings, tasks]);

  const changeDate = useCallback(
    (values: { startDate?: Date; endDate?: Date }) => {
      setDates({ ...dates, ...values });
    },
    [dates]
  );

  const [homeFilter, setHomeFilter] = useState<boolean>(true);
  const [awayFilter, setAwayFilter] = useState<boolean>(true);
  const [equipmentFilter, setEquipmentFilter] = useState<number | null>(null);
  const [teamFilter, setTeamFilter] = useState<number[]>([]);

  const context = useMemo(
    () => ({
      clubId,
      events,
      startDate: dates.startDate,
      endDate: dates.endDate,
      changeDate,
      isLoading: query.loading,
      tasks,
      homeFilter,
      setHomeFilter,
      equipmentFilter,
      setEquipmentFilter,
      teamFilter,
      setTeamFilter,
      awayFilter,
      setAwayFilter,
      displaySettings,
    }),
    [
      clubId,
      events,
      dates.startDate,
      dates.endDate,
      changeDate,
      query.loading,
      tasks,
      homeFilter,
      setHomeFilter,
      equipmentFilter,
      setEquipmentFilter,
      teamFilter,
      setTeamFilter,
      awayFilter,
      setAwayFilter,
      displaySettings,
    ]
  );

  return (
    <MatchsPlannerContext.Provider value={context}>
      {children}
    </MatchsPlannerContext.Provider>
  );
};
